import { useEffect, useState } from "react";
import axios from "axios";
import SnackBar from "../UX/SnackBar";

const ServiceBox = ({ service }) => {
  const [showDetails, setShowDetails] = useState(false);

  return (
    <div className="flex flex-col space-y-4 rounded-lg border border-gray-200 bg-white px-3 py-4">
      <h3 className="mb-2 text-left text-lg font-bold text-gray-800">
        {service.Title}
      </h3>
      <div className="grid grid-cols-2 gap-4">
        <a
          target="_blank"
          rel="noopener noreferrer"
          href={service.SessionLink}
          className="text-left text-sm text-blue-600 hover:text-blue-800 hover:underline"
        >
          View session details
        </a>
        <div className="flex items-center justify-end">
          <span className="text-sm font-semibold text-gray-600">
            {new Date(service.CompletedTimestamp).toLocaleDateString()}
          </span>
        </div>
      </div>
      <p className="text-gray-600">{service.description}</p>
      <div className="mt-3 flex justify-start">
        <button
          onClick={() => setShowDetails(!showDetails)}
          className="rounded-md bg-blue-600 px-3 py-1 text-sm font-medium text-white hover:bg-blue-700"
        >
          {showDetails === false ? "View Details" : "Close Details"}
        </button>
      </div>
      {showDetails && (
        <div className="rounded-lg border border-gray-100 bg-white p-4">
          <hr className="my-3 border-gray-300" />

          <div className="flex justify-between">
            <p className="text-gray-800">
              <span className="text-md font-bold">Seller: </span>
              <span>{service.SellerDetails.SellerName}</span>
            </p>
            <p className="text-right text-gray-800">
              <span className="font-bold">Date Completed: </span>
              {new Date(
                service.SellerDetails.DateCompleted,
              ).toLocaleDateString()}
            </p>
          </div>

          <div className="mt-4 flex flex-col space-y-3">
            <h2 className="text-md text-left font-bold text-gray-800 underline">
              Request Details:
            </h2>

            <div className="flex w-full flex-wrap justify-between rounded-md">
              <p className="text-sm font-semibold text-gray-700">
                <span>Title: </span>
                <span>{service.ServiceRequestDetails.Title}</span>
              </p>
              <p className="text-sm font-semibold text-green-700">
                <span>Price: </span>
                <span>{service.ServiceRequestDetails.Price}</span>
              </p>
            </div>

            <div className="mt-2">
              <h3 className="text-md mb-2 text-left font-bold text-gray-800">
                Description:
              </h3>
              <div
                className="rounded-md bg-gray-50 p-3 text-left text-sm text-gray-700"
                dangerouslySetInnerHTML={{
                  __html: service.ServiceRequestDetails.Description,
                }}
              ></div>
            </div>
          </div>

          {service.ServiceRequestDetails.MediaURL && (
            <div className="mt-4">
              <h3 className="text-md mb-2 font-bold text-gray-800">Media:</h3>
              <div className="flex flex-wrap gap-3">
                {service.ServiceRequestDetails.MediaUrl.map((url, index) => {
                  const fileType = url.split(".").pop().toLowerCase();
                  if (
                    ["jpg", "jpeg", "png", "gif", "webp"].includes(fileType)
                  ) {
                    return (
                      <img
                        key={index}
                        src={url}
                        alt={`Media ${index}`}
                        className="h-auto max-w-full rounded-lg shadow-md transition-transform hover:scale-105"
                      />
                    );
                  } else if (["mp4", "webm", "ogg"].includes(fileType)) {
                    return (
                      <video
                        key={index}
                        controls
                        className="max-w-full rounded-lg shadow-md"
                      >
                        <source src={url} type={`video/${fileType}`} />
                        Your browser does not support the video tag.
                      </video>
                    );
                  } else if (["mp3", "wav", "ogg"].includes(fileType)) {
                    return (
                      <audio
                        key={index}
                        controls
                        className="w-full rounded-md bg-gray-50 p-2"
                      >
                        <source src={url} type={`audio/${fileType}`} />
                        Your browser does not support the audio element.
                      </audio>
                    );
                  } else {
                    return (
                      <a
                        key={index}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block rounded-md bg-blue-50 px-4 py-2 text-blue-600 transition-colors hover:bg-blue-100"
                      >
                        View File {index + 1}
                      </a>
                    );
                  }
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

function ServicesView({ onClose }) {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(
          "https://genz-staging.feeltiptop.com/api/feed/get_services_completed",
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );

        if (response.data.status == true) {
          console.log("Services fetched successfully:", response.data.data);
          setServices(response.data.data);
        } else {
          console.log("Error fetching services:", response.data.message);
          setError(response.data.message);
          setShowErrorModal(true);
        }
      } catch (err) {
        setError(err.message);
        setShowErrorModal(true);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  // Modal component for error display
  const ErrorModal = ({ message, onClose }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-lg">
        <h2 className="mb-2 text-lg font-semibold text-red-600">Error</h2>
        <p className="mb-4 text-gray-800">{message}</p>
        <button
          className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex max-h-full w-full flex-col items-center justify-center overflow-y-auto">
      <button
        onClick={onClose}
        className="absolute right-5 top-5 rounded-lg px-3 py-2 text-right font-semibold shadow-sm"
      >
        X
      </button>
      {showErrorModal && error && (
        <ErrorModal
          message={error}
          onClose={() => {
            setShowErrorModal(false);
            setError(null);
          }}
        />
      )}

      {loading ? (
        <div className="flex h-full w-full items-center justify-center">
          <div className="flex flex-col items-center gap-2">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600"></div>
            <p className="text-gray-600">Loading services...</p>
          </div>
        </div>
      ) : services.length > 0 ? (
        <div className="h-full w-full max-w-3xl">
          <div className="mb-4 rounded-lg bg-white px-2 py-4">
            <h2 className="mb-4 text-xl font-semibold text-gray-800">
              Completed Services
            </h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-1">
              {services.map((service, index) => (
                <ServiceBox key={index} service={service} />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex w-full max-w-md flex-col items-center rounded-lg bg-white p-8 text-center shadow-sm">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="mb-4 h-16 w-16 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="text-gray-600">No completed services found.</p>
          <button className="mt-4 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">
            Add New Service
          </button>
        </div>
      )}
    </div>
  );
}

export default ServicesView;
